<template>
    <div>
        <span
            v-if="isLoading"
            class="loader-container"
        > Updating... </span>
        <input
            v-else-if="editing"
            type="text"
            :value="modelValue"
            @change="updateValue"
            @focusout="submit"
            @keypress="onReturnPress"
            ref="inputRef"
            class="custom-label-input"
        >
        <span
            v-else
            @click="toggleEdit"
            :class="acl('edit_configure_metrics') ? 'toggle-edit' : ''"
        >
            {{ modelValue }}
            <span v-if="!modelValue && !isLoading && acl('edit_configure_metrics')">
                <al-icon icon="edit" />
            </span>
        </span>
    </div>
</template>
<script setup lang="ts">
import { state } from '@/utils/state';
import type { ICellRendererParams } from '@ag-grid-community/core';
import { MetricsConfigColumnDefinitionWithLabel } from '@agyletime/performance-utils';
import { useACLs } from '@agyletime/vue-3-common/src/composables/acl';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import _ from 'lodash';
import {
    defineProps, nextTick, ref
} from 'vue';

type UpdateRowFn = (val: MetricsConfigColumnDefinitionWithLabel) => void;

const props = defineProps<{
    params: ICellRendererParams & { updateRow: UpdateRowFn };
}>();

// Helpers
const logger = useLogger('MetricLabelCustomField.vue');
const { acl } = useACLs();

// Value state
const customLabel = ref<string>(props.params.value ?? '');
const modelValue = ref(customLabel.value);

// Component state
const editing = ref(false);
const fieldId = ref<string>(props.params.data.field ?? '');
const isLoading = ref(false);
const inputRef = ref<HTMLInputElement>();

function toggleEdit(event?: Event) {
    if (!acl('edit_configure_metrics')) return;

    if (event) {
        event.preventDefault();
        event.stopPropagation();
    }

    editing.value = !editing.value;

    nextTick(() => {
        if (editing.value) inputRef.value?.focus();
    });
}

function isLabelDuplicate(value: string) {
    const node = props.params.node;
    const rowIndex = props.params.rowIndex;

    if (node && node.parent) {
        return !!node.parent.allLeafChildren.find(
            (child) =>
                value &&
                String(child.data.label).toLowerCase() === String(value).toLowerCase() &&
                // Make sure we're not comparing the current row with itself
                child.rowIndex !== rowIndex
        );
    }
    return false;
}

function getValueFromEvent(event: Event) {
    const target = event.target as HTMLInputElement;
    return String(target.value).trim();
}

function resetField() {
    const node = props.params.node;

    modelValue.value = customLabel.value || '';
    node.setDataValue('label', customLabel.value || '');
}

function updateValue(event: Event) {
    if (!acl('edit_configure_metrics')) {
        logger.debug('updateValue: failed, no permission.');
    }

    const node = props.params.node;
    const value = getValueFromEvent(event);

    logger.debug(`Updating value: '${value}'`);

    modelValue.value = value;
    node.setDataValue('label', value);
    node.data.metricDefinition.label = value;
}

// eslint-disable-next-line complexity
function submit(event: Event) {
    toggleEdit();

    // This shouldn't be shown unless something has gone wrong.
    // It's the last check before updating on the frontend.
    if (!acl('edit_configure_metrics')) {
        state.toasts.push({
            id: _.uniqueId(),
            message: "You don't have permission to update labels",
            type: 'ERROR'
        });
        resetField();
        return;
    }

    const value = getValueFromEvent(event);
    if (value === props.params.value) {
        logger.debug('Value is the same...');
        resetField();
        return;
    }

    if (isLabelDuplicate(value)) {
        state.toasts.push({
            id: _.uniqueId(),
            message: 'Custom labels must be unique',
            type: 'ERROR'
        });
        resetField();
        return;
    }

    const field = fieldId.value.trim();
    if (!field.length) {
        logger.error('Field is not defined', {
            field,
            customLabel,
            modelValue,
            inputRef
        });

        // Display an error
        state.toasts.push({
            id: _.uniqueId(),
            message: 'An error occured',
            type: 'ERROR'
        });
        return;
    }

    updateValue(event);
}

function onReturnPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
        (event.target as HTMLInputElement)?.blur?.();
    }
}
</script>
<style lang="scss">
.toggle-edit {
    cursor: pointer;
}

input.custom-label-input {
    height: 30px;
}
</style>
